import React, { useEffect, useId, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { ArrowLeft, ArrowRight, Check, Plus, ShieldCheck, Trash2, X } from "lucide-react";

import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useProfileData, buildUserUpdatePayload } from "../../hooks/useProfileData";
import { useLookupOptions } from "../../hooks/useLookupOptions";
import { updateUser } from "../../service/userService";
import AddSkillModal from "../../components/dashboard/AddSkillModal";
import { PROFILE_FIELD_LIMITS, profileFieldLengthError } from "../../utils/profileFieldLimits";
import "../../assets/css/ProfileModule.css";


const STEPS = [
  { id: 1, label: "Personal", sublabel: "& Basic Details" },
  { id: 2, label: "Career & Current", sublabel: "Organization" },
  { id: 3, label: "Previous Employers", sublabel: "(Optional)" },
];

const emptyEmployer = () => ({
  id: `emp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  companyName: "",
  jobTitle: "",
  from: "",
  to: "",
  location: "",
  description: "",
});

// Ensures an "Other" escape hatch is always selectable for lookup fields that
// have a companion other_X free-text field in the update-user payload, even
// if the backend's list doesn't already include one.
const withOther = (options) =>
  options.some((opt) => String(opt.name).toLowerCase() === "other")
    ? options
    : [...options, { id: "other", name: "Other" }];

const todayAsInputDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const TextField = ({ label, value, onChange, placeholder, type = "text", optional, readOnly, inputMode, error, onClearError, field }) => (
  <div className="pm-field mb-3">
    <label className="pm-field-label">
      {label}
      {optional && <span className="pm-optional"> (Optional)</span>}
    </label>
    <input
      type={type}
      className={`pm-input ${error ? "pm-input-error" : ""}`}
      value={value}
      placeholder={placeholder}
      onChange={(e) => { onChange(e.target.value); onClearError?.(); }}
      readOnly={readOnly}
      inputMode={inputMode}
      minLength={PROFILE_FIELD_LIMITS[field]?.min}
      maxLength={PROFILE_FIELD_LIMITS[field]?.max}
      aria-invalid={Boolean(error)}
    />
    {error && <p className="pm-field-error" role="alert">{error}</p>}
  </div>
);

// Backs a dropdown with real { id, name } records from a lookup API. Reports
// both the picked display name (what the rest of the UI shows) and its real
// backend id (what /update-user needs) in one onSelect(name, id) call. If the
// profile's current value isn't in the fetched list (e.g. it hasn't loaded
// yet), it's injected as an extra option so the real value stays visible
// instead of silently reverting to blank.
const LookupSelectField = ({ label, options, value, onSelect, optional, placeholder, loading }) => {
  const inputId = useId();
  const allOptions = !value || options.some((opt) => opt.name === value)
    ? options
    : [{ id: value, name: value }, ...options];

  return (
    <div className="pm-field mb-3">
      <label className="pm-field-label" htmlFor={inputId}>
        {label}
        {optional && <span className="pm-optional"> (Optional)</span>}
      </label>
      <Select
        inputId={inputId}
        instanceId={inputId}
        className="custom-react-select"
        classNamePrefix="custom-react-select"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }),
        }}
        options={allOptions}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => String(option.id)}
        value={allOptions.find((option) => option.name === value) || null}
        onChange={(selected) => onSelect(selected?.name || "", selected?.id ?? "")}
        isSearchable
        autoComplete="off"
        isClearable
        isLoading={loading}
        placeholder={loading ? "Loading..." : placeholder || `Select Your ${label}`}
        noOptionsMessage={() => loading ? "Loading..." : "No matching options"}
        menuPlacement="auto"
      />
    </div>
  );
};

// A lookup select that turns into a free-text input the moment "Other" is
// picked, right in the same slot. While typing, each keystroke is checked
// against the real options list: an exact match snaps the field back to a
// normal selection of that option (clearing the "other" text), so the typed
// value only ever ends up in the other_X field when it truly isn't one of
// the known choices. A small button lets the user abandon typing and go
// back to picking from the list.
const OtherCapableSelectField = ({
  label,
  options,
  value,
  otherValue,
  onSelect,
  onOtherChange,
  onRefresh,
  loading,
  placeholder,
  optional,
  field,
  error,
  onClearError,
}) => {
  const isOther = value === "Other";

  const handleTextChange = (typed) => {
    const matched = options.find(
      (opt) => opt.name !== "Other" && opt.name.trim().toLowerCase() === typed.trim().toLowerCase()
    );
    if (matched) {
      onSelect(matched.name, matched.id);
      onOtherChange("");
    } else {
      onOtherChange(typed);
    }
  };

  const handleBackToList = () => {
    onSelect("", "");
    onOtherChange("");
    onRefresh?.();
  };

  if (isOther) {
    return (
      <div className="pm-field mb-3">
        <label className="pm-field-label">
          {label}
          {optional && <span className="pm-optional"> (Optional)</span>}
        </label>
        <div className="pm-other-input-wrap">
          <input
            type="text"
            className={`pm-input ${error ? "pm-input-error" : ""}`}
            value={otherValue}
            onChange={(e) => { handleTextChange(e.target.value); onClearError?.(); }}
            placeholder={`Enter Your ${label}`}
            minLength={PROFILE_FIELD_LIMITS[field]?.min}
            maxLength={PROFILE_FIELD_LIMITS[field]?.max}
            aria-invalid={Boolean(error)}
            autoFocus
          />
          <button
            type="button"
            className="pm-other-back-btn"
            onClick={handleBackToList}
            aria-label="Choose from list instead"
          >
            <X size={14} />
          </button>
        </div>
        {error && <p className="pm-field-error" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <LookupSelectField
      label={label}
      value={value}
      onSelect={onSelect}
      options={withOther(options)}
      loading={loading}
      placeholder={placeholder}
      optional={optional}
    />
  );
};

// Our profile state stores dates as DD/MM/YYYY (matching what the API's
// fetch-user/update-user endpoints round-trip), but a native <input
// type="date"> only ever accepts/emits ISO YYYY-MM-DD - these convert
// between the two at the edge, right where the field is rendered.
const toInputDate = (displayDate) => {
  if (!displayDate) return "";
  const [d, m, y] = displayDate.split("/");
  return d && m && y ? `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}` : "";
};

const fromInputDate = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

const DateField = ({ label, value, onChange, optional, error, onClearError, onError, validate }) => (
  <div className="pm-field mb-3">
    <label className="pm-field-label">
      {label}
      {optional && <span className="pm-optional"> (Optional)</span>}
    </label>
    <input
      type="date"
      className={`pm-input ${error ? "pm-input-error" : ""}`}
      value={toInputDate(value)}
      onChange={(e) => {
        const nextValue = fromInputDate(e.target.value);
        onChange(nextValue);
        const nextError = validate?.(nextValue);
        if (nextError) onError?.(nextError);
        else onClearError?.();
      }}
      aria-invalid={Boolean(error)}
    />
    {error && <p className="pm-field-error" role="alert">{error}</p>}
  </div>
);

const TextAreaField = ({ label, value, onChange, placeholder, optional, field, error, onClearError }) => (
  <div className="pm-field mb-3">
    <label className="pm-field-label">
      {label}
      {optional && <span className="pm-optional"> (Optional)</span>}
    </label>
    <textarea
      className={`pm-input pm-textarea ${error ? "pm-input-error" : ""}`}
      rows={3}
      value={value}
      placeholder={placeholder}
      onChange={(e) => { onChange(e.target.value); onClearError?.(); }}
      minLength={PROFILE_FIELD_LIMITS[field]?.min}
      maxLength={PROFILE_FIELD_LIMITS[field]?.max}
      aria-invalid={Boolean(error)}
    />
    {error && <p className="pm-field-error" role="alert">{error}</p>}
  </div>
);

const StepPersonalDetails = ({ profile, updateProfile, onSkillsClick, lookups, onCountryChange, errors, clearError, setFieldError }) => {
  const { options, loading } = lookups;

  return (
  <div className="row g-3">
    <div className="col-md-6">
      <TextField
        label="First Name"
        field="firstName"
        value={profile.firstName}
        onChange={(v) => updateProfile({ firstName: v })}
        placeholder="Enter Your First Name"
        error={errors.firstName}
        onClearError={() => clearError("firstName")}
      />
    </div>
    <div className="col-md-6">
      <TextField
        label="Middle Name"
        field="middleName"
        optional
        value={profile.middleName}
        onChange={(v) => updateProfile({ middleName: v })}
        placeholder="Enter Your Middle Name"
        error={errors.middleName}
        onClearError={() => clearError("middleName")}
      />
    </div>
    <div className="col-md-6">
      <TextField
        label="Last Name"
        field="lastName"
        value={profile.lastName}
        onChange={(v) => updateProfile({ lastName: v })}
        placeholder="Enter Your Last Name"
        error={errors.lastName}
        onClearError={() => clearError("lastName")}
      />
    </div>

    <div className="col-md-6">
      <TextField
        label="Personal Email"
        field="personalEmail"
        type="email"
        value={profile.personalEmail}
        onChange={(v) => updateProfile({ personalEmail: v })}
        placeholder="Enter Your Personal Email"
        readOnly
        error={errors.personalEmail}
      />
    </div>
    <div className="col-md-6">
      <TextField
        label="Work Email"
        field="workEmail"
        optional
        value={profile.workEmail}
        onChange={(v) => updateProfile({ workEmail: v })}
        placeholder="Enter Your Work Email"
        type="email"
        error={errors.workEmail}
        onClearError={() => clearError("workEmail")}
      />
    </div>

    <div className="col-md-6">
      <TextField
        label="Mobile Number"
        field="mobileNumber"
        value={profile.mobileNumber}
        onChange={(v) => updateProfile({ mobileNumber: v.replace(/\D/g, "") })}
        placeholder="Enter Your Mobile Number"
        inputMode="numeric"
        error={errors.mobileNumber}
        onClearError={() => clearError("mobileNumber")}
      />
    </div>
    <div className="col-md-6">
      <DateField
        label="Date Of Birth"
        value={profile.dob}
        onChange={(v) => updateProfile({ dob: v })}
        validate={(v) => v && toInputDate(v) > todayAsInputDate() ? "Date of birth cannot be in the future." : ""}
        error={errors.dob}
        onError={(message) => setFieldError("dob", message)}
        onClearError={() => clearError("dob")}
      />
    </div>

    <div className="col-md-6">
      <LookupSelectField
        label="Gender"
        value={profile.gender}
        onSelect={(name, id) =>
          updateProfile({ gender: name, apiCodes: { ...profile.apiCodes, gender: id } })
        }
        options={options.genders}
        loading={loading}
      />
    </div>
    <div className="col-md-6">
      <TextField
        label="Pincode"
        field="pincode"
        value={profile.pincode}
        onChange={(v) => updateProfile({ pincode: v.replace(/\D/g, "") })}
        placeholder="Enter Your Pincode"
        inputMode="numeric"
        error={errors.pincode}
        onClearError={() => clearError("pincode")}
      />
    </div>

    <div className="col-md-6">
      <OtherCapableSelectField
        label="Country"
        field="otherCountry"
        value={profile.country}
        otherValue={profile.otherCountry}
        onSelect={onCountryChange}
        onOtherChange={(v) => updateProfile({ otherCountry: v })}
        error={errors.otherCountry}
        onClearError={() => clearError("otherCountry")}
        onRefresh={() => lookups.refetchList("countries")}
        options={lookups.options.countries}
        loading={lookups.loading}
      />
    </div>
    <div className="col-md-6">
      <LookupSelectField
        label="State"
        value={profile.state}
        onSelect={(name, id) =>
          updateProfile({ state: name, apiCodes: { ...profile.apiCodes, state: id } })
        }
        options={lookups.options.states}
        placeholder={profile.country ? undefined : "Select a country first"}
      />
    </div>

    <div className="col-md-6">
      <TextField
        label="City"
        field="city"
        value={profile.city}
        onChange={(v) => updateProfile({ city: v })}
        placeholder="Enter Your City"
        error={errors.city}
        onClearError={() => clearError("city")}
      />
    </div>
    <div className="col-md-6">
      <LookupSelectField
        label="Highest Education"
        value={profile.highestEducation}
        onSelect={(name, id) =>
          updateProfile({ highestEducation: name, apiCodes: { ...profile.apiCodes, highest_education: id } })
        }
        options={lookups.options.highestEducations}
        loading={lookups.loading}
      />
    </div>

    <div className="col-md-6">
      <LookupSelectField
        label="Annual Earnings"
        value={profile.annualEarnings}
        onSelect={(name, id) =>
          updateProfile({ annualEarnings: name, apiCodes: { ...profile.apiCodes, household_income: id } })
        }
        options={lookups.options.householdIncomes}
        loading={lookups.loading}
      />
    </div>
    <div className="col-md-6">
      <TextField
        label="Mailing Address"
        field="mailingAddress"
        value={profile.mailingAddress}
        onChange={(v) => updateProfile({ mailingAddress: v })}
        placeholder="Enter Your Mailing Address"
        error={errors.mailingAddress}
        onClearError={() => clearError("mailingAddress")}
      />
    </div>

    <div className="col-12">
      <label className="pm-field-label">Skills</label>
      <div className="pm-skills-input d-flex align-items-center flex-wrap gap-2">
        {profile.skills.map((skill) => (
          <span key={skill} className="pm-chip pm-chip-selected">
            {skill}
            <button
              type="button"
              onClick={() => updateProfile({ skills: profile.skills.filter((s) => s !== skill) })}
              aria-label={`Remove ${skill}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <button type="button" className="pm-skill-add-btn" onClick={onSkillsClick} aria-label="Add skill">
          <Plus size={16} />
        </button>
      </div>
    </div>
  </div>
  );
};

const StepCareerDetails = ({ profile, updateProfile, lookups, onSectorChange, errors, clearError }) => {
  const { options, loading } = lookups;

  const lookupField = (fieldKey, codeKey) => ({
    onSelect: (name, id) =>
      updateProfile({ [fieldKey]: name, apiCodes: { ...profile.apiCodes, [codeKey]: id } }),
  });

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <LookupSelectField
          label="Work Status"
          value={profile.workStatus}
          options={options.workStatuses}
          loading={loading}
          {...lookupField("workStatus", "work_status")}
        />
      </div>
      <div className="col-md-6">
        <LookupSelectField
          label="Employment Status"
          value={profile.employmentStatus}
          options={options.employmentStatuses}
          loading={loading}
          {...lookupField("employmentStatus", "employement_status")}
        />
      </div>

      <div className="col-md-6">
        <LookupSelectField
          label="Profession Type"
          value={profile.professionType}
          options={options.professions}
          loading={loading}
          {...lookupField("professionType", "profession_type")}
        />
      </div>
      <div className="col-md-6">
        <TextField
          label="Job Title"
          field="jobTitle"
          value={profile.jobTitle}
          onChange={(v) => updateProfile({ jobTitle: v })}
          placeholder="Enter Your Job Title"
          error={errors.jobTitle}
          onClearError={() => clearError("jobTitle")}
        />
      </div>

      <div className="col-md-6">
        <OtherCapableSelectField
          label="Company Name"
          field="otherCompany"
          value={profile.companyName}
          otherValue={profile.otherCompany}
          onOtherChange={(v) => updateProfile({ otherCompany: v })}
          error={errors.otherCompany}
          onClearError={() => clearError("otherCompany")}
          onRefresh={() => lookups.refetchList("companyNames")}
          options={options.companyNames}
          loading={loading}
          {...lookupField("companyName", "company_name")}
        />
      </div>
      <div className="col-md-6">
        <LookupSelectField
          label="Total Work Experience"
          value={profile.totalExperience}
          options={options.workExperiences}
          loading={loading}
          {...lookupField("totalExperience", "work_experience")}
        />
      </div>

      <div className="col-md-6">
        <LookupSelectField
          label="Seniority Level"
          value={profile.seniorityLevel}
          options={options.seniorityLevels}
          loading={loading}
          {...lookupField("seniorityLevel", "seniority_level")}
        />
      </div>
      <div className="col-md-6">
        <TextField
          label="Job Responsibility"
          field="jobResponsibility"
          optional
          value={profile.jobResponsibility}
          onChange={(v) => updateProfile({ jobResponsibility: v })}
          placeholder="Enter Job Responsibility (Optional)"
          error={errors.jobResponsibility}
          onClearError={() => clearError("jobResponsibility")}
        />
      </div>

      <div className="col-md-6">
        <LookupSelectField
          label="Tenure In Current Company"
          value={profile.tenure}
          options={options.tenureOptions}
          loading={loading}
          {...lookupField("tenure", "work_ex_c_company")}
        />
      </div>

      <div className="col-md-6">
        <OtherCapableSelectField
          label="Sector"
          field="otherSector"
          value={profile.sector}
          otherValue={profile.otherSector}
          onSelect={onSectorChange}
          onOtherChange={(v) => updateProfile({ otherSector: v })}
          error={errors.otherSector}
          onClearError={() => clearError("otherSector")}
          onRefresh={() => lookups.refetchList("sectors")}
          options={options.sectors}
          loading={loading}
        />
      </div>
      <div className="col-md-6">
        <OtherCapableSelectField
          label="Industry"
          field="otherIndustry"
          value={profile.industry}
          otherValue={profile.otherIndustry}
          onOtherChange={(v) => updateProfile({ otherIndustry: v })}
          error={errors.otherIndustry}
          onClearError={() => clearError("otherIndustry")}
          onRefresh={() => lookups.loadIndustriesForSector(profile.apiCodes?.sector)}
          options={options.industries}
          placeholder={profile.sector ? undefined : "Select a sector first"}
          {...lookupField("industry", "industry")}
        />
      </div>

      <div className="col-md-6">
        <LookupSelectField
          label="Department"
          value={profile.department}
          options={options.departments}
          loading={loading}
          {...lookupField("department", "department")}
        />
      </div>

      <div className="col-md-6">
        <LookupSelectField
          label="Business Model"
          value={profile.businessModel}
          options={options.businessModels}
          loading={loading}
          {...lookupField("businessModel", "business_model")}
        />
      </div>
      <div className="col-md-6">
        <LookupSelectField
          label="Company Revenue"
          value={profile.companyRevenue}
          options={options.companyRevenues}
          loading={loading}
          {...lookupField("companyRevenue", "company_revenue")}
        />
      </div>

      <div className="col-md-6">
        <LookupSelectField
          label="Company Type"
          value={profile.companyType}
          options={options.companyTypes}
          loading={loading}
          {...lookupField("companyType", "company_type")}
        />
      </div>
      <div className="col-md-6">
        <LookupSelectField
          label="Company Size"
          value={profile.companySize}
          options={options.companySizes}
          loading={loading}
          {...lookupField("companySize", "company_size")}
        />
      </div>

      <div className="col-md-6">
        <TextField
          label="Headquarters Location"
          field="hqLocation"
          value={profile.hqLocation}
          onChange={(v) => updateProfile({ hqLocation: v })}
          placeholder="Enter Your Headquarters Location"
          error={errors.hqLocation}
          onClearError={() => clearError("hqLocation")}
        />
      </div>
      <div className="col-md-6">
        <TextField
          label="Company LinkedIn Page"
          field="companyLinkedIn"
          optional
          value={profile.companyLinkedIn}
          onChange={(v) => updateProfile({ companyLinkedIn: v })}
          placeholder="Enter Your Company LinkedIn Page"
          error={errors.companyLinkedIn}
          onClearError={() => clearError("companyLinkedIn")}
        />
      </div>

      <div className="col-12">
        <TextAreaField
          label="About Company"
          field="aboutCompany"
          optional
          value={profile.aboutCompany}
          onChange={(v) => updateProfile({ aboutCompany: v })}
          placeholder="Enter A Brief Description Of Your Company"
          error={errors.aboutCompany}
          onClearError={() => clearError("aboutCompany")}
        />
      </div>
    </div>
  );
};

const StepPreviousEmployers = ({ employers, onChange, errors, clearError, setFieldError }) => {
  const updateEmployer = (id, patch) => {
    onChange(employers.map((emp) => (emp.id === id ? { ...emp, ...patch } : emp)));
  };

  const removeEmployer = (id) => {
    onChange(employers.filter((emp) => emp.id !== id));
  };

  const addEmployer = () => {
    onChange([...employers, emptyEmployer()]);
  };

  return (
    <div>
      {employers.map((emp, index) => (
        <div key={emp.id} className="pm-employer-block mb-3">
          <div className="pm-employer-header d-flex align-items-center justify-content-between">
            <span>Previous Employer {index + 1}</span>
            {employers.length > 1 && (
              <button
                type="button"
                className="pm-employer-remove"
                onClick={() => removeEmployer(emp.id)}
                aria-label="Remove employer"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <TextField
                label="Company Name"
                field="employerCompanyName"
                value={emp.companyName}
                onChange={(v) => updateEmployer(emp.id, { companyName: v })}
                placeholder="Enter Your Company Name"
                error={errors[`employers.${emp.id}.companyName`]}
                onClearError={() => clearError(`employers.${emp.id}.companyName`)}
              />
            </div>
            <div className="col-md-6">
              <TextField
                label="Job Title"
                field="employerJobTitle"
                value={emp.jobTitle}
                onChange={(v) => updateEmployer(emp.id, { jobTitle: v })}
                placeholder="Enter Your Job Title"
                error={errors[`employers.${emp.id}.jobTitle`]}
                onClearError={() => clearError(`employers.${emp.id}.jobTitle`)}
              />
            </div>

            <div className="col-md-4">
              <DateField
                label="From"
                value={emp.from}
                onChange={(v) => updateEmployer(emp.id, { from: v })}
                validate={(v) => v && toInputDate(v) > todayAsInputDate() ? "From date cannot be in the future." : ""}
                error={errors[`employers.${emp.id}.from`]}
                onError={(message) => setFieldError(`employers.${emp.id}.from`, message)}
                onClearError={() => clearError(`employers.${emp.id}.from`)}
              />
            </div>
            <div className="col-md-4">
              <DateField
                label="To"
                value={emp.to}
                onChange={(v) => updateEmployer(emp.id, { to: v })}
                validate={(v) => {
                  if (v && toInputDate(v) > todayAsInputDate()) return "To date cannot be in the future.";
                  if (v && emp.from && toInputDate(v) < toInputDate(emp.from)) return "To date cannot be earlier than the From date.";
                  return "";
                }}
                error={errors[`employers.${emp.id}.to`]}
                onError={(message) => setFieldError(`employers.${emp.id}.to`, message)}
                onClearError={() => clearError(`employers.${emp.id}.to`)}
              />
            </div>
            <div className="col-md-4">
              <TextField
                label="Location"
                field="employerLocation"
                value={emp.location}
                onChange={(v) => updateEmployer(emp.id, { location: v })}
                placeholder="Enter Location (City/State)"
                error={errors[`employers.${emp.id}.location`]}
                onClearError={() => clearError(`employers.${emp.id}.location`)}
              />
            </div>

            <div className="col-12">
              <TextAreaField
                label="Job Description / Responsibilities"
                field="employerDescription"
                optional
                value={emp.description}
                onChange={(v) => updateEmployer(emp.id, { description: v })}
                placeholder="Brief Description Of Your Job Responsibilities"
                error={errors[`employers.${emp.id}.description`]}
                onClearError={() => clearError(`employers.${emp.id}.description`)}
              />
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="pm-add-employer-link" onClick={addEmployer}>
        + Add Another Employer
      </button>
    </div>
  );
};

const ProfileSetup = () => {
  const navigate = useNavigate();
  const { firstName, lastName, user } = useCurrentUser();
  const { profile, updateProfile, refetch } = useProfileData({ firstName, lastName, email: user?.personal_email || user?.email || user?.useremail });
  const lookups = useLookupOptions();

  const [step, setStep] = useState(1);
  const [skillsModalOpen, setSkillsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (step === 3 && profile.previousEmployers.length === 0) {
      updateProfile({ previousEmployers: [emptyEmployer()] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Single source of truth for the two cascading lists: whenever the parent
  // field's real code changes - whether from the user picking a new value or
  // from the account's existing country/sector loading in from fetch-user -
  // this is the only place that fetches the dependent list. (It used to also
  // be fired directly from the change handlers below, but that meant two
  // identical requests went out for the same selection; the app's axios
  // layer cancels the older of two identical in-flight requests, and the
  // cancelled one's error gets swallowed into a resolved `undefined`, which
  // could then overwrite the real states/industries with an empty list.)
  useEffect(() => {
    lookups.loadStatesForCountry(profile.apiCodes?.country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.apiCodes?.country]);

  useEffect(() => {
    lookups.loadIndustriesForSector(profile.apiCodes?.sector);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.apiCodes?.sector]);

  const handleCountryChange = (name, id) => {
    updateProfile({
      country: name,
      state: "",
      apiCodes: { ...profile.apiCodes, country: id, state: "" },
    });
  };

  const handleSectorChange = (name, id) => {
    updateProfile({
      sector: name,
      industry: "",
      apiCodes: { ...profile.apiCodes, sector: id, industry: "" },
    });
  };

  const clearError = (field) => setErrors((current) => ({ ...current, [field]: "" }));
  const setFieldError = (field, message) => setErrors((current) => ({ ...current, [field]: message }));

  const showValidationErrors = (validationErrors) => {
    setErrors(validationErrors);
    toast.warn(Object.values(validationErrors)[0], { toastId: "profile-validation" });
    requestAnimationFrame(() => {
      document.querySelector(".pm-field-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const validateCurrentStep = () => {
    const validationErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const namePattern = /^[A-Za-z\s]+$/;

    if (step === 1) {
      ["firstName", "middleName", "lastName", "personalEmail", "workEmail", "mobileNumber", "pincode", "city", "mailingAddress"].forEach((field) => {
        const error = profileFieldLengthError(field, profile[field]);
        if (error) validationErrors[field] = error;
      });
      if (profile.country === "Other") {
        validationErrors.otherCountry = profileFieldLengthError("otherCountry", profile.otherCountry)
          || (!profile.otherCountry.trim() ? "Other country is required." : "");
        if (!validationErrors.otherCountry) delete validationErrors.otherCountry;
      }
      if (!profile.firstName.trim()) validationErrors.firstName = "First name is required.";
      else if (!validationErrors.firstName && !namePattern.test(profile.firstName.trim())) validationErrors.firstName = "First name can contain letters only.";
      if (profile.middleName.trim() && !validationErrors.middleName && !namePattern.test(profile.middleName.trim())) validationErrors.middleName = "Middle name can contain letters only.";
      if (!profile.lastName.trim()) validationErrors.lastName = "Last name is required.";
      else if (!validationErrors.lastName && !namePattern.test(profile.lastName.trim())) validationErrors.lastName = "Last name can contain letters only.";
      if (!profile.personalEmail.trim()) validationErrors.personalEmail = "Personal email is required.";
      else if (!validationErrors.personalEmail && !emailPattern.test(profile.personalEmail.trim())) validationErrors.personalEmail = "Enter a valid personal email address.";
      if (profile.workEmail && !validationErrors.workEmail && !emailPattern.test(profile.workEmail.trim())) validationErrors.workEmail = "Enter a valid work email address.";
      if (profile.mobileNumber && !/^\d+$/.test(profile.mobileNumber)) validationErrors.mobileNumber = "Mobile number can contain digits only.";
      if (profile.pincode && !/^\d+$/.test(profile.pincode)) validationErrors.pincode = "Pincode can contain digits only.";
      if (profile.dob && toInputDate(profile.dob) > todayAsInputDate()) validationErrors.dob = "Date of birth cannot be in the future.";
    }

    if (step === 2) {
      ["jobTitle", "jobResponsibility", "hqLocation", "companyLinkedIn", "aboutCompany"].forEach((field) => {
        const error = profileFieldLengthError(field, profile[field]);
        if (error) validationErrors[field] = error;
      });
      [["companyName", "otherCompany"], ["sector", "otherSector"], ["industry", "otherIndustry"]].forEach(([selection, field]) => {
        if (profile[selection] !== "Other") return;
        const error = profileFieldLengthError(field, profile[field]);
        if (error) validationErrors[field] = error;
        else if (!profile[field].trim()) validationErrors[field] = `${PROFILE_FIELD_LIMITS[field].label} is required.`;
      });
    }

    if (step === 3) {
      profile.previousEmployers.forEach((employer) => {
        const hasAnyValue = Object.entries(employer).some(([key, value]) => key !== "id" && Boolean(value));
        if (!hasAnyValue) return;
        if (!employer.companyName.trim()) validationErrors[`employers.${employer.id}.companyName`] = "Company name is required for this employer.";
        else {
          const error = profileFieldLengthError("employerCompanyName", employer.companyName);
          if (error) validationErrors[`employers.${employer.id}.companyName`] = error;
        }
        if (!employer.jobTitle.trim()) validationErrors[`employers.${employer.id}.jobTitle`] = "Job title is required for this employer.";
        else {
          const error = profileFieldLengthError("employerJobTitle", employer.jobTitle);
          if (error) validationErrors[`employers.${employer.id}.jobTitle`] = error;
        }
        [["location", "employerLocation"], ["description", "employerDescription"]].forEach(([field, rule]) => {
          const error = profileFieldLengthError(rule, employer[field]);
          if (error) validationErrors[`employers.${employer.id}.${field}`] = error;
        });
        const from = toInputDate(employer.from);
        const to = toInputDate(employer.to);
        if (from && from > todayAsInputDate()) validationErrors[`employers.${employer.id}.from`] = "From date cannot be in the future.";
        if (to && to > todayAsInputDate()) validationErrors[`employers.${employer.id}.to`] = "To date cannot be in the future.";
        else if (from && to && to < from) validationErrors[`employers.${employer.id}.to`] = "To date cannot be earlier than the From date.";
      });
    }

    if (Object.keys(validationErrors).length) {
      showValidationErrors(validationErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const goNext = () => {
    if (!validateCurrentStep()) return;
    setStep((s) => Math.min(s + 1, 3));
  };
  const goPrevious = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step < 3) {
      goNext();
      return;
    }

    if (!validateCurrentStep()) return;

    setSaving(true);
    try {
      await updateUser(buildUserUpdatePayload(profile));
      await refetch();

      await Swal.fire({
        title: "Profile updated",
        text: "Your profile details have been saved.",
        icon: "success",
        confirmButtonColor: "#c72430",
      });

      navigate("/settings/profile");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't save your profile. Please try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-search-results profile-module">
      <div className="row g-3">
        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <h1 className="sr-page-title">Profile Setup</h1>
            <p className="sr-page-subtitle">
              Complete Your Profile To Get More Survey Invitations And Earn More Coins.
            </p>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <div className="pm-stepper d-flex align-items-start">
              {STEPS.map((s, idx) => {
                const status = s.id < step ? "done" : s.id === step ? "current" : "upcoming";
                return (
                  <React.Fragment key={s.id}>
                    <div className={`pm-step pm-step-${status}`}>
                      <div className="pm-step-circle">{status === "done" ? <Check size={14} /> : s.id}</div>
                      <div className="pm-step-label">
                        <div>{s.label}</div>
                        <div className="pm-step-sublabel">{s.sublabel}</div>
                      </div>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`pm-step-connector ${s.id < step ? "done" : ""}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <form onSubmit={handleSubmit} noValidate>
              {step === 1 && (
                <StepPersonalDetails
                  profile={profile}
                  updateProfile={updateProfile}
                  onSkillsClick={() => setSkillsModalOpen(true)}
                  lookups={lookups}
                  onCountryChange={handleCountryChange}
                  errors={errors}
                  clearError={clearError}
                  setFieldError={setFieldError}
                />
              )}
              {step === 2 && (
                <StepCareerDetails
                  profile={profile}
                  updateProfile={updateProfile}
                  lookups={lookups}
                  onSectorChange={handleSectorChange}
                  errors={errors}
                  clearError={clearError}
                />
              )}
              {step === 3 && (
                <StepPreviousEmployers
                  employers={profile.previousEmployers}
                  onChange={(employers) => updateProfile({ previousEmployers: employers })}
                  errors={errors}
                  clearError={clearError}
                  setFieldError={setFieldError}
                />
              )}

              <div className="pm-security-note d-flex align-items-center gap-2 mt-3">
                <ShieldCheck size={14} /> Your information is secure and will not be shared.
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                {step > 1 ? (
                  <button type="button" className="pm-outline-btn" onClick={goPrevious}>
                    <ArrowLeft size={16} /> Previous
                  </button>
                ) : (
                  <span />
                )}

                <button type="submit" className="pm-primary-btn" disabled={saving}>
                  {step === 3 ? (saving ? "Saving..." : "Submit") : "Next"} <ArrowRight size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {skillsModalOpen && (
        <AddSkillModal
          skills={profile.skills}
          onClose={() => setSkillsModalOpen(false)}
          onSave={(skills) => {
            updateProfile({ skills });
            setSkillsModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ProfileSetup;
